import { supabase } from './supabase';

interface TransactionData {
  transaction_id: string;
  montant: number;
  currency?: string;
  phone_number?: string;
  user_name?: string;
  user_id?: string;
  verification_date?: string;
}

/**
 * Enregistre une nouvelle transaction dans la base de données
 * @param transactionId - L'identifiant de la transaction
 * @param montant - Le montant de la transaction
 * @param currency - La devise de la transaction (par défaut: XAF)
 * @param phoneNumber - Le numéro de téléphone de l'émetteur
 * @param userName - Le nom de l'utilisateur émetteur
 * @returns L'ID de l'enregistrement créé ou null en cas d'erreur
 */
export const saveTransaction = async (
  transactionId: string, 
  montant: number,
  currency: string = 'XAF',
  phoneNumber?: string,
  userName?: string
): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('transaction_amounts')
      .insert({
        transaction_id: transactionId,
        montant: montant,
        currency: currency,
        phone_number: phoneNumber,
        user_name: userName
      })
      .select('id')
      .single();
    
    if (error) {
      console.error('[Transaction] Erreur lors de l\'enregistrement:', error);
      return null;
    }
    
    console.log(`[Transaction] Transaction ${transactionId} enregistrée avec succès`);
    return data?.id || null;
  } catch (error) {
    console.error('[Transaction] Exception lors de l\'enregistrement:', error);
    return null;
  }
};

/**
 * Enregistre une nouvelle transaction à partir des données extraites d'un message
 * @param message - Le message contenant les détails de la transaction
 * @returns L'ID de l'enregistrement créé ou null en cas d'erreur
 */
export const saveTransactionFromMessage = async (message: string): Promise<string | null> => {
  try {
    // Extraction des informations du message
    const phoneMatch = message.match(/par (\d+)/);
    const userMatch = message.match(/par \d+ ([A-Z\s]+) to/);
    const amountMatch = message.match(/Montant de transaction : (\d+(?:\.\d+)?) (\w+)/);
    const idMatch = message.match(/ID transaction : ([A-Z0-9\.]+)/);
    
    if (!phoneMatch || !userMatch || !amountMatch || !idMatch) {
      console.error('[Transaction] Impossible d\'extraire toutes les informations du message');
      return null;
    }
    
    const phoneNumber = phoneMatch[1];
    const userName = userMatch[1].trim();
    const amount = parseFloat(amountMatch[1]);
    const currency = amountMatch[2];
    const transactionId = idMatch[1];
    
    return await saveTransaction(transactionId, amount, currency, phoneNumber, userName);
  } catch (error) {
    console.error('[Transaction] Erreur lors de l\'extraction et de l\'enregistrement:', error);
    return null;
  }
};

/**
 * Récupère une transaction par son ID
 * @param transactionId - L'identifiant de la transaction à rechercher
 * @returns Les détails de la transaction ou null si non trouvée
 */
export const getTransactionById = async (transactionId: string): Promise<TransactionData | null> => {
  try {
    // Utiliser limit(1) et orderBy pour s'assurer de ne récupérer qu'une seule transaction
    // Trier par created_at pour récupérer la plus récente en cas de duplicatas
    const { data: transaction, error } = await supabase
      .from('transaction_amounts')
      .select('*')
      .eq('transaction_id', transactionId)
      .is('user_id', null)  // Prendre uniquement les transactions non utilisées
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      console.error('[Transaction] Erreur lors de la récupération:', error);
      return null;
    }
    
    // Ne pas lier automatiquement l'utilisateur ici pour éviter le conflit avec markTransactionAsUsed
    return transaction;
  } catch (error) {
    console.error('[Transaction] Exception lors de la récupération:', error);
    return null;
  }
};

/**
 * Récupère toutes les transactions
 * @returns Liste des transactions ou tableau vide en cas d'erreur
 */
export const getAllTransactions = async (): Promise<TransactionData[]> => {
  try {
    const { data, error } = await supabase
      .from('transaction_amounts')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('[Transaction] Erreur lors de la récupération des transactions:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('[Transaction] Exception lors de la récupération des transactions:', error);
    return [];
  }
};

/**
 * Vérifie si une transaction est valide pour un montant donné
 * @param transactionId - L'identifiant de la transaction
 * @param expectedAmount - Le montant attendu
 * @returns Vrai si la transaction est valide, faux sinon
 */
export const validateTransaction = async (transactionId: string, expectedAmount: number): Promise<boolean> => {
  const transaction = await getTransactionById(transactionId);
  if (!transaction) {
    return false;
  }
  
  return transaction.montant === expectedAmount;
}; 

/**
 * Marque une transaction comme utilisée par un utilisateur spécifique, au lieu de la supprimer
 * @param transactionId - L'identifiant de la transaction à marquer comme utilisée
 * @returns Vrai si l'opération a réussi, faux sinon
 */
export const markTransactionAsUsed = async (transactionId: string): Promise<boolean> => {
  try {
    console.log(`[Transaction] Marquage de la transaction ${transactionId} comme utilisée`);
    
    // Récupérer l'utilisateur actuel
    const { data: userData } = await supabase.auth.getUser();
    if (!userData || !userData.user) {
      console.error('[Transaction] Utilisateur non connecté');
      return false;
    }
    
    // Récupérer d'abord la transaction pour vérifier si elle n'est pas déjà utilisée
    // Utiliser limit(1) et orderBy pour s'assurer de ne récupérer qu'une seule transaction
    const { data: existingTransaction, error: fetchError } = await supabase
      .from('transaction_amounts')
      .select('user_id, montant')
      .eq('transaction_id', transactionId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();  // Utiliser maybeSingle pour éviter les erreurs si aucune ligne n'est trouvée
      
    if (fetchError) {
      console.error('[Transaction] Erreur lors de la récupération de la transaction:', fetchError);
      return false;
    }
      
    if (!existingTransaction) {
      console.error('[Transaction] Transaction non trouvée');
      return false;
    }
    
    // Si la transaction a déjà un user_id, vérifier si c'est le même utilisateur
    if (existingTransaction.user_id) {
      // Si c'est le même utilisateur, considérer l'opération comme réussie
      if (existingTransaction.user_id === userData.user.id) {
        console.log(`[Transaction] Transaction ${transactionId} déjà associée à cet utilisateur, considérée comme utilisée avec succès`);
        return true;
      } else {
        console.error('[Transaction] Transaction déjà utilisée par un autre utilisateur');
        return false;
      }
    }
    
    // Utiliser la fonction RPC link_user_to_transaction au lieu de la mise à jour directe
    // pour éviter les problèmes d'ambiguïté de colonne
    const { error } = await supabase.rpc('link_user_to_transaction', {
      p_transaction_id: transactionId,
      p_user_id: userData.user.id
    });
    
    if (error) {
      console.error('[Transaction] Erreur lors du marquage:', error);
      return false;
    }
    
    // Vérifier si l'utilisateur est parrainé
    const { data: userProfile } = await supabase
      .from('profiles')
      .select('referred_by')
      .eq('id', userData.user.id)
      .single();
      
    if (userProfile?.referred_by) {
      console.log('[Transaction] Utilisateur parrainé, la commission sera calculée automatiquement par le trigger SQL');
      // Le trigger SQL process_referral_commission s'occupera du calcul
    }
    
    return true;
  } catch (error) {
    console.error('[Transaction] Exception lors du marquage:', error);
    return false;
  }
};

/**
 * Supprime une transaction par son ID
 * Cette fonction est maintenue pour compatibilité, mais il est préférable
 * d'utiliser markTransactionAsUsed à la place
 * @param transactionId - L'identifiant de la transaction à supprimer
 * @returns Vrai si la suppression a réussi, faux sinon
 * @deprecated Utiliser markTransactionAsUsed à la place
 */
export const deleteTransactionById = async (transactionId: string): Promise<boolean> => {
  try {
    console.log(`[Transaction] Tentative de marquage de la transaction ${transactionId} comme utilisée (ancienne méthode de suppression)`);
    
    // Utilisez directement markTransactionAsUsed pour la cohérence
    return await markTransactionAsUsed(transactionId);
  } catch (error) {
    console.error('[Transaction] Exception lors de la suppression:', error);
    return false;
  }
}; 